import { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';

// ── Types (mirror API response) ─────────────────────────────────────────────

type EUSize = 'S' | 'M' | 'L' | 'XL';
type Verdict = 'ACCURATE' | 'OVER_ESTIMATED' | 'UNDER_ESTIMATED';

interface EUSizeStats {
  count: number;
  avgExpectedMs: number;
  avgActualMs: number;
  avgAccuracyPercent: number;
  suggestedAdjustment: string | null;
}

interface AccuracyRecord {
  sessionId: string;
  issueId: string | null;
  issueTitle: string | null;
  euSize: EUSize;
  expectedDurationMs: number;
  actualDurationMs: number;
  accuracyPercent: number;
  verdict: Verdict;
  completedAt: string;
}

interface CalibrationReport {
  projectId: string;
  generatedAt: string;
  periodStart: string;
  periodEnd: string;
  totalSessions: number;
  accurateCount: number;
  overEstimatedCount: number;
  underEstimatedCount: number;
  overallAccuracyPercent: number;
  byEUSize: Partial<Record<EUSize, EUSizeStats>>;
  records: AccuracyRecord[];
  recommendations: string[];
}

interface InsufficientDataResponse {
  insufficientData: true;
  message: string;
  totalSessions: number;
}

type CalibrationResponse = CalibrationReport | InsufficientDataResponse;

// ── Helpers ─────────────────────────────────────────────────────────────────

function msToMin(ms: number): string {
  return (ms / 60_000).toFixed(1);
}

function accuracyColor(pct: number): string {
  if (pct >= 75) return '#22c55e';
  if (pct >= 50) return '#eab308';
  return '#ef4444';
}

function verdictStyle(verdict: Verdict): { bg: string; color: string; label: string } {
  switch (verdict) {
    case 'ACCURATE':        return { bg: 'rgba(34,197,94,0.12)',  color: '#22c55e', label: 'Accurate' };
    case 'OVER_ESTIMATED':  return { bg: 'rgba(96,165,250,0.12)', color: '#60a5fa', label: 'Over' };
    case 'UNDER_ESTIMATED': return { bg: 'rgba(239,68,68,0.12)',  color: '#ef4444', label: 'Under' };
  }
}

// ── Styles ───────────────────────────────────────────────────────────────────

const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 16px', fontFamily: 'var(--font-body)',
  fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500,
};

const tdStyle: React.CSSProperties = {
  padding: '10px 16px', fontFamily: 'var(--font-body)', fontSize: 13,
  color: 'var(--text)',
};

const cardStyle: React.CSSProperties = {
  background: 'var(--surface)', borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border)', padding: 20,
};

// ── Page ─────────────────────────────────────────────────────────────────────

export default function CalibrationReportPage() {
  const { projectId } = useParams<{ projectId: string }>();
  const [data, setData] = useState<CalibrationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = useCallback(() => {
    if (!projectId) return;
    setLoading(true);
    apiFetch<CalibrationResponse>(
      `/projects/${projectId}/phases/phase_4_development/execute/calibration-report?days=${days}`,
    )
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [projectId, days]);

  useEffect(() => { load(); }, [load]); // eslint-disable-line react-hooks/set-state-in-effect

  const isInsufficient = data && 'insufficientData' in data && data.insufficientData;
  const report = data && !isInsufficient ? (data as CalibrationReport) : null;

  return (
    <main style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <Link
              to={`/projects/${projectId}`}
              style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text-secondary)', textDecoration: 'none' }}
            >
              ← Dashboard
            </Link>
            <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 24, fontWeight: 700, color: 'var(--text)', marginTop: 6 }}>
              Estimation Calibration
            </h1>
          </div>
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            data-testid="days-selector"
            style={{
              fontFamily: 'var(--font-body)', fontSize: 13, padding: '6px 12px',
              background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-sm)', cursor: 'pointer',
            }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>All time</option>
          </select>
        </div>

        {/* Loading */}
        {loading && (
          <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)' }}>Loading calibration data…</p>
        )}

        {/* Insufficient data */}
        {!loading && isInsufficient && (
          <div style={{ maxWidth: 400, margin: '80px auto', textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>📊</div>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, fontWeight: 600, color: 'var(--text)', marginBottom: 8 }}>
              Calibration not yet available
            </h2>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 14, color: 'var(--text-secondary)', marginBottom: 16 }}>
              Estimation calibration requires at least 3 completed Dev Agent sessions.
              You have {(data as InsufficientDataResponse).totalSessions} so far.
            </p>
            <div style={{ width: 200, margin: '0 auto', background: 'var(--border)', borderRadius: 4, height: 6 }}>
              <div
                style={{
                  width: `${Math.min(((data as InsufficientDataResponse).totalSessions / 3) * 100, 100)}%`,
                  background: '#8b5cf6', borderRadius: 4, height: 6, transition: 'width 0.3s',
                }}
              />
            </div>
            <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>
              {(data as InsufficientDataResponse).totalSessions} / 3 sessions
            </p>
          </div>
        )}

        {/* Full report */}
        {!loading && report && (
          <>
            {/* Summary cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24 }}>
              <div style={cardStyle} data-testid="summary-overall">
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Overall Accuracy</p>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700, color: accuracyColor(report.overallAccuracyPercent) }}>
                  {report.overallAccuracyPercent.toFixed(1)}%
                </p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {report.totalSessions} sessions analysed
                </p>
              </div>
              <div style={cardStyle} data-testid="summary-accurate">
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Accurate Estimates</p>
                <p style={{ fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700, color: 'var(--text)' }}>
                  {report.accurateCount} / {report.totalSessions}
                </p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  {report.totalSessions > 0 ? ((report.accurateCount / report.totalSessions) * 100).toFixed(0) : 0}% hit rate
                </p>
              </div>
              <div style={cardStyle} data-testid="summary-under">
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginBottom: 4 }}>Under-Estimated</p>
                <p style={{
                  fontFamily: 'var(--font-display)', fontSize: 32, fontWeight: 700,
                  color: report.underEstimatedCount > report.overEstimatedCount ? '#ef4444' : 'var(--text)',
                }}>
                  {report.underEstimatedCount}
                </p>
                <p style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                  took longer than predicted
                </p>
              </div>
            </div>

            {/* EU Size breakdown */}
            <div style={{ ...cardStyle, marginBottom: 24, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)' }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                  Accuracy by EU Size
                </h2>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="eu-size-table">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={thStyle}>EU Size</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Sessions</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Avg Expected</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Avg Actual</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Accuracy</th>
                    <th style={thStyle}>Adjustment</th>
                  </tr>
                </thead>
                <tbody>
                  {(['S', 'M', 'L', 'XL'] as EUSize[]).map(size => {
                    const stats = report.byEUSize[size];
                    if (!stats || stats.count === 0) return null;
                    return (
                      <tr key={size} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{size}</td>
                        <td style={{ ...tdStyle, textAlign: 'right' }}>{stats.count}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)' }}>{msToMin(stats.avgExpectedMs)} min</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)' }}>{msToMin(stats.avgActualMs)} min</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: accuracyColor(stats.avgAccuracyPercent) }}>
                          {stats.avgAccuracyPercent.toFixed(1)}%
                        </td>
                        <td style={{ ...tdStyle, fontSize: 12, color: '#eab308' }}>
                          {stats.suggestedAdjustment ?? '—'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Recommendations */}
            {report.recommendations.length > 0 && (
              <div style={{ ...cardStyle, marginBottom: 24, border: '1px solid rgba(234,179,8,0.25)' }} data-testid="recommendations">
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: '#eab308', marginBottom: 10 }}>
                  Recommendations
                </h2>
                <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
                  {report.recommendations.map((rec, i) => (
                    <li key={i} style={{ fontFamily: 'var(--font-body)', fontSize: 13, color: 'var(--text)', padding: '4px 0', display: 'flex', gap: 8, alignItems: 'baseline' }}>
                      <span style={{ color: '#eab308' }}>•</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Session detail */}
            <div style={{ ...cardStyle, padding: 0, overflow: 'hidden' }}>
              <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, fontWeight: 600, color: 'var(--text)' }}>
                  Session Detail
                </h2>
                <span style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                  {report.records.length} records
                </span>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }} data-testid="session-detail-table">
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    <th style={thStyle}>Story</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>EU</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Expected</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Actual</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Accuracy</th>
                    <th style={{ ...thStyle, textAlign: 'center' }}>Verdict</th>
                    <th style={{ ...thStyle, textAlign: 'right' }}>Date</th>
                  </tr>
                </thead>
                <tbody>
                  {report.records.map(record => {
                    const v = verdictStyle(record.verdict);
                    return (
                      <tr key={record.sessionId} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ ...tdStyle, maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {record.issueTitle ?? record.issueId ?? '—'}
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600 }}>{record.euSize}</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)' }}>{msToMin(record.expectedDurationMs)} min</td>
                        <td style={{ ...tdStyle, textAlign: 'right', color: 'var(--text-secondary)' }}>{msToMin(record.actualDurationMs)} min</td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 600, color: accuracyColor(record.accuracyPercent) }}>
                          {record.accuracyPercent.toFixed(1)}%
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'center' }}>
                          <span style={{
                            display: 'inline-block', fontSize: 11, fontWeight: 500, padding: '2px 8px',
                            borderRadius: 12, background: v.bg, color: v.color,
                          }}>
                            {v.label}
                          </span>
                        </td>
                        <td style={{ ...tdStyle, textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>
                          {new Date(record.completedAt).toLocaleDateString()}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
    </main>
  );
}
